import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { useIsCanvasReadOnlyForToolTab } from '../FlowComponents/WorkflowRightToolbar/toolTabReadOnly.store';
import { useWorkflowEditorMode } from '../FlowComponents/WorkflowRightToolbar/workflowEditorMode.store';

/**
 * The Tool tab wraps the workflow as it is, so nothing can add a node there. `guard` returns true
 * when adding is blocked and tells the person to switch to Canvas.
 */
export function useToolTabAddNodeGuard() {
  const { t } = useTranslation();
  const { workflowEditorMode } = useWorkflowEditorMode();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const isAddingNodesBlocked = workflowEditorMode === WorkflowEditorMode.AppBuilder;

  const guard = useCallback(() => {
    if (!isAddingNodesBlocked) return false;
    updateSnackbarData({
      text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_ADD_NODES_IN_TOOL_TAB),
      severity: 'default',
      icon: null,
      isOpen: true,
    });
    return true;
  }, [isAddingNodesBlocked, t, updateSnackbarData]);

  return { isAddingNodesBlocked, guard };
}

/**
 * The Tool tab is read-only: nodes only move there. `guard` refuses deleting and locking, and tells
 * the person to switch to Canvas. Off whenever the feature flag is.
 */
export function useToolTabEditGuard() {
  const { t } = useTranslation();
  const isReadOnly = useIsCanvasReadOnlyForToolTab();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);

  const guard = useCallback(() => {
    if (!isReadOnly) return false;
    updateSnackbarData({
      text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_EDIT_IN_TOOL_TAB),
      severity: 'default',
      icon: null,
      isOpen: true,
    });
    return true;
  }, [isReadOnly, t, updateSnackbarData]);

  return { guard };
}
